import { useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import ReadonlyField from '../../components/ReadonlyField/ReadonlyField'
import { ContractHonorariumDetailDTO, ContractHonorariumOptionDTO } from '../../dto/contractHonorarium/ContractHonorariumDTO'
import { getContractHonorarium } from '../../services/ContractHonorariumController'
import { parseApiError } from '../../services/apiError'
import { formatMoney } from '../../utils/money'
import { contractKindLabel, formatIsoDate, formatPercentage, isFixedAmount, RETENTION_CONTRACT_HELP, STATUS_LABELS } from './contractHonorariumFormat'

interface ContractHonorariumDetailViewProps {
  contractHonorariumId: number
  distributionTypes: ContractHonorariumOptionDTO[]
  onBack: () => void
  onEdit: () => void
  onError: (message: string) => void
  onStale: (message: string) => void
}

const TITLE = 'Distribución'

/** Una dimensión vacía aplica a cualquier valor, como en el listado. */
function orAll(value: string | null, allLabel: string): string {
  return value && value.trim() ? value : allLabel
}

/**
 * Vista de detalle (DS §3.7): los datos de la distribución como texto, con las mismas secciones que el formulario,
 * y "Editar" para pasar a la edición.
 */
export default function ContractHonorariumDetailView({
  contractHonorariumId,
  distributionTypes,
  onBack,
  onEdit,
  onError,
  onStale,
}: Readonly<ContractHonorariumDetailViewProps>) {
  const [detail, setDetail] = useState<ContractHonorariumDetailDTO | null>(null)

  useEffect(() => {
    let cancelled = false
    getContractHonorarium(contractHonorariumId)
      .then((loaded) => {
        if (!cancelled) setDetail(loaded)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const apiError = parseApiError(error)
        if (apiError.errorCode === 'CONTRACT_HONORARIUM_NOT_FOUND') onStale(apiError.message)
        else {
          onError('No se pudo cargar la distribución. Intentá de nuevo.')
          onBack()
        }
      })
    return () => {
      cancelled = true
    }
  }, [contractHonorariumId])

  const backAction = <Button type='button' link icon='pi pi-angle-double-left' label='Volver' onClick={onBack} />

  if (!detail) {
    return (
      <div className='nv-scope nv-page chd-view chd-form-page'>
        <header className='nv-page-head'>
          {backAction}
          <h1 className='nv-h1'>{TITLE}</h1>
        </header>
        <div className='md-loading' role='status'>
          <ProgressSpinner strokeWidth='4' />
          <span>Cargando…</span>
        </div>
      </div>
    )
  }

  const distributionTypeName = distributionTypes.find((type) => type.id === detail.distributionTypeId)?.name

  return (
    <div className='nv-scope nv-page chd-view chd-form-page'>
      <header className='nv-page-head'>
        <div className='nv-head-row'>
          {backAction}
          <Button type='button' className='buttonMain2' outlined icon='pi pi-pencil' label='Editar' onClick={onEdit} />
        </div>
        <h1 className='nv-h1'>{TITLE}</h1>
        <span className='nv-subtitle'>{STATUS_LABELS[detail.status]}</span>
      </header>

      <div className='chd-form'>
        <h2 className='md-section-title'>Alcance</h2>
        <div className='chd-readonly-row'>
          <ReadonlyField label='Emisor de facturación'>{orAll(detail.billingEntityName, 'Todos')}</ReadonlyField>
          <ReadonlyField label='Obra social'>{orAll(detail.healthEntityName, 'Todas')}</ReadonlyField>
          <ReadonlyField label='Plan'>{orAll(detail.healthEntityPlanName, 'Todos')}</ReadonlyField>
          <ReadonlyField label='Profesional'>{orAll(detail.professionalName, 'Todos')}</ReadonlyField>
          <ReadonlyField label='Nomenclador'>{orAll(detail.nomenclatorName, 'Todos')}</ReadonlyField>
          <ReadonlyField label='Edificio'>{orAll(detail.buildingName, 'Todos')}</ReadonlyField>
        </div>

        <h2 className='md-section-title'>Distribución</h2>
        <div className='chd-readonly-row'>
          <ReadonlyField label='Tipo de contrato'>{contractKindLabel(detail.billingEntityProfessional)}</ReadonlyField>
          <ReadonlyField label='Tipo de distribución'>{distributionTypeName}</ReadonlyField>
        </div>
        {detail.billingEntityProfessional && (
          <span className='chd-hint'>
            <i className='pi pi-info-circle' aria-hidden='true' />
            {RETENTION_CONTRACT_HELP}
          </span>
        )}
        {isFixedAmount(detail.distributionTypeId) ? (
          <div className='chd-readonly-row'>
            <ReadonlyField label='Importe fijo'>{formatMoney(detail.fixedAmount)}</ReadonlyField>
          </div>
        ) : (
          <div className='chd-readonly-row'>
            <ReadonlyField label='Porcentaje de honorarios'>{formatPercentage(detail.honoPercentage)}</ReadonlyField>
            <ReadonlyField label='Porcentaje de coseguro'>{formatPercentage(detail.copaymentPercentage)}</ReadonlyField>
            <ReadonlyField label='Porcentaje de diferencial'>{formatPercentage(detail.diferentialPercentage)}</ReadonlyField>
          </div>
        )}

        <h2 className='md-section-title'>Vigencia</h2>
        <div className='chd-readonly-row'>
          <ReadonlyField label='Vigente desde'>{formatIsoDate(detail.validFrom)}</ReadonlyField>
          <ReadonlyField label='Vigente hasta'>{formatIsoDate(detail.validUntil)}</ReadonlyField>
        </div>
      </div>
    </div>
  )
}
